import React, { useCallback, useEffect, useState } from 'react'
import { Pencil, Plus, Trash2, X } from 'lucide-react'
import { FaTimes } from 'react-icons/fa'
import server from '../../../api/server'
import { useToast } from '../../../context/ToastContext'

const EMPTY_EVENT_FORM = { event_id: null, event_name: '', event_status: [] }

const formatEventUpdatedAt = (value) => {
  if(!value){
    return '-'
  }
  const date = new Date(Number(value) * 1000)
  if(Number.isNaN(date.getTime())){
    return '-'
  }
  return date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' })
}

const ClientEventsConfiguration = ({ client_id, service_id, email }) => {
  const { showToast } = useToast()
  const [events, setEvents] = useState([])
  const [loadingEvents, setLoadingEvents] = useState(false)
  const [eventModalOpen, setEventModalOpen] = useState(false)
  const [eventForm, setEventForm] = useState(EMPTY_EVENT_FORM)
  const [statusInput, setStatusInput] = useState('')
  const [savingEvent, setSavingEvent] = useState(false)
  const [eventToDelete, setEventToDelete] = useState(null)
  const [deletingEvent, setDeletingEvent] = useState(false)
  const [blockingReferences, setBlockingReferences] = useState([])

  const getEventConfigurations = useCallback(async (clientId, serviceId, userEmail) => {
    try {
      setLoadingEvents(true)
      let res = await server.getClientEventConfigurations(clientId, serviceId, userEmail, 'developer-hub', 'configuration')
      let resp = await res.json();
      if(res.status === 200){
        setEvents(resp.data || [])
      }else{
        showToast(resp.error || 'Unable to load events', 'error')
      }
    } catch (error) {
      showToast('Unable to load events', 'error')
    }
    setLoadingEvents(false)
  }, [showToast])

  useEffect(() => {
    if(client_id && service_id && email){
      getEventConfigurations(client_id, service_id, email)
    }
  }, [client_id, service_id, email, getEventConfigurations])

  const openCreateEventModal = () => {
    setEventForm(EMPTY_EVENT_FORM)
    setStatusInput('')
    setBlockingReferences([])
    setEventModalOpen(true)
  }

  const openEditEventModal = (event) => {
    setEventForm({ event_id: event.event_id, event_name: event.event_name, event_status: [...(event.event_status || [])] })
    setStatusInput('')
    setBlockingReferences([])
    setEventModalOpen(true)
  }

  const closeEventModal = () => {
    if(savingEvent) return
    setEventModalOpen(false)
    setBlockingReferences([])
  }

  const addStatusValue = () => {
    const value = statusInput.trim()
    if(!value){
      return
    }
    if(value.includes('"') || value.includes("'")){
      showToast('Status values cannot contain quotes', 'error')
      return
    }
    if(eventForm.event_status.some(status => status.toLowerCase() === value.toLowerCase())){
      showToast('This status is already added', 'error')
      return
    }
    setEventForm(prev => ({ ...prev, event_status: [...prev.event_status, value] }))
    setStatusInput('')
  }

  const removeStatusValue = (value) => {
    setEventForm(prev => ({ ...prev, event_status: prev.event_status.filter(status => status !== value) }))
  }

  const saveEventConfiguration = async () => {
    const eventName = eventForm.event_name.trim()
    if(!eventName){
      showToast('Event name is required', 'error')
      return
    }
    if(eventForm.event_status.length === 0){
      showToast('Add at least one event status', 'error')
      return
    }
    try {
      setSavingEvent(true)
      setBlockingReferences([])
      let res = await server.saveClientEventConfiguration({
        client_id,
        service_id,
        email,
        menu: 'developer-hub',
        submenu: 'configuration',
        action: eventForm.event_id ? 'update' : 'create',
        event_id: eventForm.event_id,
        event_name: eventName,
        event_status: eventForm.event_status
      })
      let resp = await res.json();
      if(res.status === 200){
        showToast(eventForm.event_id ? 'Event updated successfully' : 'Event created successfully', 'success')
        setEventModalOpen(false)
        getEventConfigurations(client_id, service_id, email)
      }else{
        setBlockingReferences(resp.references || [])
        showToast(resp.error || 'Unable to save event', 'error')
      }
    } catch (error) {
      showToast('Unable to save event', 'error')
    }
    setSavingEvent(false)
  }

  const deleteEventConfiguration = async () => {
    if(!eventToDelete){
      return
    }
    try {
      setDeletingEvent(true)
      setBlockingReferences([])
      let res = await server.saveClientEventConfiguration({
        client_id,
        service_id,
        email,
        menu: 'developer-hub',
        submenu: 'configuration',
        action: 'delete',
        event_id: eventToDelete.event_id
      })
      let resp = await res.json();
      if(res.status === 200){
        showToast('Event deleted successfully', 'success')
        setEventToDelete(null)
        getEventConfigurations(client_id, service_id, email)
      }else{
        setBlockingReferences(resp.references || [])
        showToast(resp.error || 'Unable to delete event', 'error')
      }
    } catch (error) {
      showToast('Unable to delete event', 'error')
    }
    setDeletingEvent(false)
  }

  const renderBlockingReferences = () => blockingReferences.length > 0 && (
    <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
      <div className="font-medium mb-1">Custom module logic still uses these values:</div>
      <ul className="list-disc pl-5 space-y-0.5">
        {blockingReferences.map((reference, index) => (
          <li key={`${reference.parameter}-${reference.value}-${index}`}>
            {reference.parameter} ({reference.section}) uses "{reference.value}"
          </li>
        ))}
      </ul>
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Events</h2>
            <p className="text-sm text-gray-500 mt-1">Manage events your systems send to update customer data.</p>
          </div>
          <button
            onClick={openCreateEventModal}
            className="inline-flex items-center justify-center px-4 py-2 rounded text-white font-medium bg-indigo-600 hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Event
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated By</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loadingEvents && (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-sm text-gray-500">Loading events...</td>
                </tr>
              )}
              {!loadingEvents && events.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-sm text-gray-500">No events configured for this service.</td>
                </tr>
              )}
              {!loadingEvents && events.map((event) => (
                <tr key={event.event_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-700 font-mono">{event.event_id}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{event.event_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    <div className="flex flex-wrap gap-1.5">
                      {(event.event_status || []).map(status => (
                        <span key={status} className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs">{status}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{event.updated_by || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{formatEventUpdatedAt(event.updatedat)}</td>
                  <td className="px-6 py-4 text-sm">
                    <div className="inline-flex items-center gap-3">
                      <button onClick={() => openEditEventModal(event)} className="text-indigo-600 hover:text-indigo-800" title="Edit event" aria-label="Edit event">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { setBlockingReferences([]); setEventToDelete(event) }}
                        className="text-red-600 hover:text-red-800"
                        title="Delete event"
                        aria-label="Delete event"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {eventModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center px-4 py-6" style={{ zIndex: 99999999999999 }}>
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <div className="relative bg-white rounded-lg shadow-xl border w-full max-w-xl mx-4">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{eventForm.event_id ? 'Edit Event' : 'Add Event'}</h2>
                <p className="text-sm text-gray-500 mt-1">Name the event and list every status it can have.</p>
              </div>
              <button onClick={closeEventModal} className="inline-flex items-center justify-center text-slate-500 hover:text-slate-700" title="Close" disabled={savingEvent}>
                <FaTimes className="h-4 w-4" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {eventForm.event_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Event ID</label>
                  <input type="text" value={eventForm.event_id} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Event Name</label>
                <input
                  type="text"
                  value={eventForm.event_name}
                  onChange={(e) => setEventForm(prev => ({ ...prev, event_name: e.target.value }))}
                  placeholder="e.g. Appointment"
                  maxLength={100}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Event Status</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="text"
                    value={statusInput}
                    onChange={(e) => setStatusInput(e.target.value)}
                    onKeyDown={(e) => { if(e.key === 'Enter'){ e.preventDefault(); addStatusValue() } }}
                    placeholder="e.g. Booked, then press Enter"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-indigo-500 focus:border-indigo-500"
                  />
                  <button onClick={addStatusValue} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Add</button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {eventForm.event_status.length === 0 && <span className="text-xs text-gray-400">No statuses added yet.</span>}
                  {eventForm.event_status.map(status => (
                    <span key={status} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-indigo-50 text-indigo-700 text-xs">
                      {status}
                      <button onClick={() => removeStatusValue(status)} className="text-indigo-400 hover:text-indigo-700" title={`Remove ${status}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              {renderBlockingReferences()}
            </div>

            <div className="flex justify-end gap-3 px-6 pb-6">
              <button
                onClick={closeEventModal}
                disabled={savingEvent}
                className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                style={{height: "40px", borderRadius: "4px"}}
              >
                Cancel
              </button>
              <button
                onClick={saveEventConfiguration}
                disabled={savingEvent}
                className={`px-6 py-2 rounded-lg bg-indigo-600 text-white ${savingEvent ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700'}`}
                style={{height: "40px", borderRadius: "4px"}}
              >
                {savingEvent ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {eventToDelete && (
        <div className="fixed inset-0 flex items-center justify-center px-4 py-6" style={{ zIndex: 99999999999999 }}>
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
            <div className="flex justify-between items-center p-4 pb-2">
              <h2 className="text-xl text-gray-700" style={{fontWeight: "550"}}>Delete event?</h2>
              <button onClick={() => setEventToDelete(null)} className="text-gray-500 hover:text-gray-700" disabled={deletingEvent}>
                <FaTimes style={{color: "slategray"}}/>
              </button>
            </div>

            <div className="p-6 pt-2 space-y-3">
              <p className="text-gray-500 text-sm">
                Are you sure you want to delete "{eventToDelete.event_name}"? New requests for this event will be rejected. Events already received stay in customer history.
              </p>
              {renderBlockingReferences()}
            </div>

            <div className="flex justify-end gap-3 p-6 pt-0">
              <button
                onClick={() => setEventToDelete(null)}
                disabled={deletingEvent}
                className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                style={{height: "40px", borderRadius: "4px"}}
              >
                No
              </button>
              <button
                onClick={deleteEventConfiguration}
                disabled={deletingEvent}
                className={`px-6 py-2 rounded-lg bg-indigo-600 text-white ${deletingEvent ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700'}`}
                style={{height: "40px", borderRadius: "4px"}}
              >
                {deletingEvent ? 'Deleting...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientEventsConfiguration
